/* Copyright 2021 Mozilla Foundation
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * A subset of the l10n strings in the `l10n/zh-CN/viewer.properties` file.
 */
const DEFAULT_L10N_STRINGS = {
  of_pages: '/ {{pagesCount}}',
  page_of_pages: '({{pageNumber}} of {{pagesCount}})',

  document_properties_kb: '{{size_kb}} KB ({{size_b}} bytes)',
  document_properties_mb: '{{size_mb}} MB ({{size_b}} bytes)',
  document_properties_date_string: '{{date}}, {{time}}',
  document_properties_page_size_unit_inches: '英寸',
  document_properties_page_size_unit_millimeters: '毫米',
  document_properties_page_size_orientation_portrait: '纵向',
  document_properties_page_size_orientation_landscape: '横向',
  document_properties_page_size_name_a3: 'A3',
  document_properties_page_size_name_a4: 'A4',
  document_properties_page_size_name_letter: '文本',
  document_properties_page_size_name_legal: '法律',
  document_properties_page_size_dimension_string:
    '{{width}} × {{height}} {{unit}} ({{orientation}})',
  document_properties_page_size_dimension_name_string:
    '{{width}} × {{height}} {{unit}} ({{name}}, {{orientation}})',
  document_properties_linearized_yes: '是',
  document_properties_linearized_no: '否',

  print_progress_percent: '{{progress}}%',

  'toggle_sidebar.title': '切换侧栏',
  'toggle_sidebar_notification2.title': '切换侧栏（文档所含的大纲/附件/图层）',

  additional_layers: '其他图层',
  page_canvas: '页码 {{page}}',
  thumb_page_title: '页码 {{page}}',
  thumb_page_canvas: '页面 {{page}} 的缩略图',

  find_reached_top: '到达文档开头，从末尾继续',
  find_reached_bottom: '到达文档末尾，从开头继续',
  'find_match_count[one]': '第 {{current}} 项，共匹配 {{total}} 项',
  'find_match_count[other]': '第 {{current}} 项，共匹配 {{total}} 项',
  'find_match_count_limit[one]': '超过 {{limit}} 项匹配',
  'find_match_count_limit[other]': '超过 {{limit}} 项匹配',
  find_not_found: '找不到指定词语',

  error_version_info: 'PDF.js v{{version}} (build: {{build}})',
  error_message: '信息：{{message}}',
  error_stack: '堆栈: {{stack}}',
  error_file: '文件: {{file}}',
  error_line: '行号: {{line}}',
  rendering_error: '渲染页面时发生错误。',

  page_scale_width: '适合页宽',
  page_scale_fit: '适合页面',
  page_scale_auto: '自动缩放',
  page_scale_actual: '实际大小',
  page_scale_percent: '{{scale}}%',

  loading_error: '载入 PDF 时发生错误。',
  invalid_file_error: '无效或损坏的 PDF 文件。',
  missing_file_error: '缺少 PDF 文件。',
  unexpected_response_error: '意外的服务器响应。',

  printing_not_supported: '警告：此浏览器尚未完整支持打印功能。',
  printing_not_ready: '警告：此 PDF 未完成载入，无法打印。',
  web_fonts_disabled: 'Web 字体已被禁用：无法使用嵌入的 PDF 字体。',
}

function getL10nFallback(key, args) {
  switch (key) {
    case 'find_match_count':
      key = `find_match_count[${args.total === 1 ? 'one' : 'other'}]`
      break
    case 'find_match_count_limit':
      key = `find_match_count_limit[${args.limit === 1 ? 'one' : 'other'}]`
      break
  }
  return DEFAULT_L10N_STRINGS[key] || ''
}

// Replaces {{arguments}} with their values.
function formatL10nValue(text, args) {
  if (!args) {
    return text
  }
  return text.replace(/\{\{\s*(\w+)\s*\}\}/g, (all, name) => {
    return name in args ? args[name] : '{{' + name + '}}'
  })
}

/**
 * No-op implementation of the localization service.
 * @implements {IL10n}
 */
const NullL10n = {
  async getLanguage() {
    return 'zh-cn'
  },

  async getDirection() {
    return 'ltr'
  },

  async get(key, args = null, fallback = getL10nFallback(key, args)) {
    return formatL10nValue(fallback, args)
  },

  async translate(element) {},
}

export { getL10nFallback, NullL10n }
